<script lang="ts">
	import type { Coordinates } from '$lib/components/map/types';
	import type { ItineraryStore } from '$lib/components/search/itinerary/ItineraryStore';
	import { page } from '$app/state';
	import { ItineraryTrafficMode } from '$houdini';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import BackHomeHeader from '$lib/components/header/BackHomeHeader.svelte';
	import ItineraryOptions from '$lib/components/itinerary/options/ItineraryOptions.svelte';
	import { tripOptionsCarName } from '$lib/components/itinerary/tripOptions';
	import { computeSearchParamsItinerary } from '$lib/components/itinerary/util';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import { addNotification } from '$lib/components/notifications/notifications';
	import ItineraryStack from '$lib/components/search/itinerary/ItineraryStack.svelte';
	import { MAX_STEPS } from '$lib/components/search/itinerary/ItineraryStore';
	import {
		computeMyLocation,
		isGeolocationGranted,
		resetMyLocation,
	} from '$lib/components/search/itinerary/userLocation';
	import { searchExtensionTouchpoint } from '$lib/services/searchExtension';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import { booleanToString, filterDefaultCarId, trafficModeIsWithTraffic } from '$lib/services/tracking/googleAnalytics/tracking';
	import { getSearchParamsStore, gotoWithParams } from '$lib/services/urls/searchParams';
	import * as m from '$paraglide/messages';
	import { onMount } from 'svelte';
	import { intersect } from 'svelte-intersection-observer-action';

	interface Props {
		proximity: Coordinates;
		itineraryStore: ItineraryStore;
		isItineraryAlreadyComputed?: boolean;
		isSearchActiveOnMobile: boolean;
		active: boolean;
		onClose: (params: { toTrackBackHome: boolean }) => void;
	}

	let {
		proximity,
		itineraryStore,
		isItineraryAlreadyComputed = false,
		isSearchActiveOnMobile,
		active = $bindable(),
		onClose,
	}: Props = $props();

	const { itineraryResultsStore } = $derived(page.data);

	let titleSearchInput: string | undefined = $state();
	const searchParams = getSearchParamsStore();
	const { tripOptionsStore, clickedPosition } = getMapLayoutContext();

	const itinerary = $derived(itineraryStore?.itinerary);
	const isItineraryComputable = $derived(!!(
		$itinerary?.length > 1
		&& $itinerary[0].coordinates
		&& $itinerary[$itinerary.length - 1].coordinates
	));

	const isMaxSteps = $derived($itinerary?.length >= MAX_STEPS);

	onMount(async () => {
		const initialWaypoint = $itinerary[0];
		const geolocationGranted = await isGeolocationGranted();

		if (!$itinerary[0].coordinates && geolocationGranted) {
			itineraryStore.updateWaypoint({
				...initialWaypoint,
				coordinates: 'MY_LOC',
				label: m.itinerary__current_location(),
				isMyLocation: true,
			});

			// reload id to force the input to update
			// with the new label
			itineraryStore.reloadId(initialWaypoint);

			// Start computing location of the user
			computeMyLocation();
		}
	});

	function computeTripOtions() {
		const {
			currency,
			energyPrice,
			distanceSystem,
			travelMode,
			tripConstraint,
			withCaravan,
			traffic,
		} = $tripOptionsStore;
		return {
			travelMode,
			distanceSystem,
			tripConstraint:
				travelMode === 'CAR' || travelMode === 'MOTORCYCLE' ? tripConstraint : undefined,
			car: travelMode === 'CAR' ? $tripOptionsStore.car : undefined,
			energyPrice,
			currency,
			withCaravan: travelMode === 'CAR' ? withCaravan : undefined,
			traffic:
				travelMode === 'CAR' || travelMode === 'MOTORCYCLE'
					? traffic || ItineraryTrafficMode.CLOSINGS
					: undefined,
		};
	}

	async function compute() {
		if (isItineraryComputable) {
			try {
				$clickedPosition = undefined;
				itineraryResultsStore?.reset();

				const tripOptions = computeTripOtions();
				const searchParamsItinerary = await computeSearchParamsItinerary($itinerary);
				const departure = $itinerary[0].label;
				const arrival = $itinerary[$itinerary.length - 1].label;
				const isElectricVehicle = $tripOptionsStore.car?.isEv;
				track({
					action: 'itinerary_compute',
					payload: {
						action: 'click',
						car_name: tripOptions.car ? tripOptionsCarName.serialize(tripOptions.car) : undefined,
						car_id: filterDefaultCarId(tripOptions.car),
						city_departure: departure,
						city_arrival: arrival,
						unit: tripOptions.distanceSystem === 'IMPERIAL' ? 'mi' : 'm',
						// TODO: Add currency when fuel price is ready
						travel_mode: tripOptions.travelMode,
						route_preference: tripOptions.tripConstraint,
						drive_with_electric_vehicle: booleanToString(!!isElectricVehicle),
						drive_with_caravan: booleanToString(!!tripOptions.withCaravan),
						drive_with_traffic: booleanToString(trafficModeIsWithTraffic(tripOptions.traffic)),
						range_rating_alert: isElectricVehicle
							? $tripOptionsStore.rechargeTreshold?.toString()
							: undefined,
						price_kwh: isElectricVehicle ? tripOptions.energyPrice?.toString() : undefined,
					},
				});

				const itineraryResultsParams = {
					...$searchParams,
					from: departure,
					to: arrival,
					itinerary: searchParamsItinerary,
					...tripOptions,
					selectedRoute: 0,
					rechargeTreshold: $tripOptionsStore.rechargeTreshold,
					zoiSettings: {
						...$searchParams.zoiSettings,
						containsEvOnly: isElectricVehicle,
					},
				};

				searchExtensionTouchpoint.activate('compute');
				await gotoWithParams(`/routes/results`, itineraryResultsParams);
			} catch {
				// TODO this also catches non-location errors
				addNotification(m.location_control__error());
				resetMyLocation();
			}
		}
	}

	function reverseSteps() {
		track({
			action: 'itinerary_step',
			payload: {
				action: 'reverse',
			},
		});
		itineraryStore.reverse();
	}

	function addStep() {
		track({
			action: 'itinerary_step',
			payload: {
				action: 'add',
			},
		});
		itineraryStore.addWayPoint();
	}

	let itineraryStackIsInViewport = $state(true);
</script>

<div class="p-2 md:p-2 md:hidden">
	<div class="flex flex-row h-10">
		<Button
			variant="filled"
			color="light"
			size="md"
			icon="west"
			onclick={() => !isSearchActiveOnMobile && onClose({ toTrackBackHome: true })}
			text={(!isSearchActiveOnMobile && m.action__back()) || undefined}
		/>
		{#if isSearchActiveOnMobile}
			<p class="case-upper text-center flex-grow-1 flex-self-center font-size-3.5 pr-11">{titleSearchInput}</p>
		{/if}
	</div>
</div>
<div class="hidden md:block md:px-2">
	<BackHomeHeader onBackHome={() => onClose({ toTrackBackHome: false })} />
</div>
<div
	class="flex flex-col w-full p-2 md:px-2 z-hover"
	use:intersect={{
		callback(entry) {
			itineraryStackIsInViewport = entry.isIntersecting;
		},
	}}
>
	<ItineraryStack {proximity} {itineraryStore} bind:active onActiveWaypointSearch={({ inputName }) => titleSearchInput = inputName} />

	{#if !isSearchActiveOnMobile}
		<div class="flex flex-row flex-wrap w-full mt-2 justify-between">
			{#if !isMaxSteps}
				<Button
					variant="ghost"
					size="md"
					icon="add"
					text={m.itinerary__add_step()}
					onclick={addStep}
				/>
			{:else}
				<Button
					variant="ghost"
					size="md"
					icon="info"
					text={m.itinerary__max_steps_reached()}
					disabled
				/>
			{/if}
			<Button
				variant="ghost"
				size="md"
				icon="swap"
				text={m.itinerary__invert()}
				onclick={reverseSteps}
			/>
		</div>
	{/if}
</div>
<div
	class={['sticky flex md:justify-end top-0 px-2 py-3 md:px-4 bg-neutral-weakest z-5', itineraryStackIsInViewport ? '' : 'shadow-bottom-md']}
>
	<Button
		variant="filled"
		color="primary"
		size="md"
		text={isItineraryAlreadyComputed ? m.itinerary__recompute() : m.itinerary__compute()}
		class="w-full md:w-auto"
		disabled={!isItineraryComputable}
		onclick={compute}
	/>
</div>
{#if !isSearchActiveOnMobile}
	<div class="md:px-2 mt-2">
		<ItineraryOptions carIdFromParams={$searchParams.car?.id} />
	</div>
{/if}
